import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Input,
    FormGroup,
    Spinner
} from "reactstrap";
import { Line } from "react-chartjs-2";
import Header from "components/Headers/Header.js";
import axios from "axios";
import { getToken } from "services/authService";
import { chartOptions, parseOptions } from "variables/charts.js";

const AdManagerHourlyReport = () => {
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [dimension, setDimension] = useState("general");
    const [dimensionValue, setDimensionValue] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterOptions, setFilterOptions] = useState([]);

    // UTM Filtering States
    const [utmPrefix, setUtmPrefix] = useState("");

    const [totalRevenue, setTotalRevenue] = useState(0);
    const [pointsCount, setPointsCount] = useState(0);

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        setDimensionValue("");
        setUtmPrefix(""); // Reset prefix
        if (dimension !== 'general') {
            fetchOptions();
        } else {
            setFilterOptions([]);
        }
    }, [dimension]);

    useEffect(() => {
        fetchData();
    }, [dimension, dimensionValue, selectedDate]);

    // Extrai prefixos únicos das UTMs
    const utmPrefixes = useMemo(() => {
        if (dimension !== 'utm' || filterOptions.length === 0) return [];
        const prefixes = new Set();
        filterOptions.forEach(opt => {
            const parts = opt.split('-');
            if (parts.length > 0) prefixes.add(parts[0]);
        });
        return Array.from(prefixes).sort();
    }, [dimension, filterOptions]);

    // Filtra as opções exibidas com base no prefixo selecionado
    const displayOptions = useMemo(() => {
        if (dimension === 'utm' && utmPrefix) {
            return filterOptions.filter(opt => opt.startsWith(utmPrefix + '-'));
        }
        return filterOptions;
    }, [dimension, utmPrefix, filterOptions]);

    // Seleciona automaticamente o primeiro item ao mudar o prefixo
    useEffect(() => {
        if (dimension === 'utm' && utmPrefix && displayOptions.length > 0) {
            setDimensionValue(displayOptions[0]);
        }
    }, [utmPrefix, displayOptions, dimension]);

    const fetchOptions = async () => {
        try {
            const token = getToken();
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.get(`${API_URL}/admanager/hourly/options?dimension=${dimension}`, config);
            console.log('📥 [Frontend] Opções recebidas para', dimension, ':', res.data);
            const options = res.data || [];
            setFilterOptions(options);

            // Auto-select logic
            if (options.length > 0) {
                // Se for UTM, tenta setar o prefixo do primeiro item
                if (dimension === 'utm') {
                    const firstPrefix = options[0].split('-')[0];
                    setUtmPrefix(firstPrefix);
                    // O useEffect acima vai setar o dimensionValue
                } else {
                    setDimensionValue(options[0]);
                }
            }
        } catch (error) {
            console.error("Erro ao buscar opções:", error);
            setFilterOptions([]);
        }
    };

    const fetchData = async () => {
        if (dimension !== 'general' && !dimensionValue) {
            setChartData(null);
            setTotalRevenue(0);
            setPointsCount(0);
            return;
        }

        setLoading(true);
        try {
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            let url = `${API_URL}/admanager/hourly?date=${selectedDate}&dimension=${dimension}&_t=${Date.now()}`;
            if (dimensionValue) {
                url += `&value=${dimensionValue}`;
            }

            console.log('🔵 [Frontend] Fazendo request:', url);

            const res = await axios.get(url, config);
            const { chartData: dataPoints, totalRevenue, pointsCount, source } = res.data;

            const labels = dataPoints.map(point => point.x);
            const values = dataPoints.map(point => point.y);

            const dimensionLabel = dimensionValue ? ` - ${dimensionValue}` : '';
            const sourceLabel = source === 'ad_manager_api' ? ' (Tempo Real)' : '';

            const newChartData = {
                labels: labels,
                datasets: [
                    {
                        label: `Receita Acumulada - ${selectedDate}${dimensionLabel}${sourceLabel}`,
                        data: values,
                        borderColor: '#5e72e4',
                        backgroundColor: 'rgba(94, 114, 228, 0.1)',
                        fill: true,
                        tension: 0.4
                    }
                ]
            };

            setChartData(newChartData);
            setTotalRevenue(totalRevenue);
            setPointsCount(pointsCount);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
        } finally {
            setLoading(false);
        }
    };

    if (window.Chart) {
        parseOptions(window.Chart, chartOptions());
    }

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <Col className="mb-5 mb-xl-0" xl="12">
                        <Card className="shadow">
                            <CardHeader className="bg-transparent">
                                <Row className="align-items-center">
                                    <Col>
                                        <h6 className="text-uppercase text-light ls-1 mb-1">
                                            Visão Geral
                                        </h6>
                                        <h2 className="text-white mb-0">Receita Acumulada por Hora</h2>
                                    </Col>
                                    <Col className="text-right d-flex justify-content-end align-items-center">

                                        {/* Seletor de Data */}
                                        <FormGroup className="mb-0 mr-2" style={{ minWidth: '160px' }}>
                                            <Input
                                                type="date"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                className="form-control-alternative"
                                            />
                                        </FormGroup>

                                        {/* Seletor de Dimensão */}
                                        <FormGroup className="mb-0 mr-2" style={{ minWidth: '140px' }}>
                                            <Input
                                                type="select"
                                                value={dimension}
                                                onChange={(e) => setDimension(e.target.value)}
                                                className="form-control-alternative"
                                            >
                                                <option value="general">Geral</option>
                                                <option value="domain">Por Site</option>
                                                <option value="utm">Por UTM</option>
                                            </Input>
                                        </FormGroup>

                                        {/* Filtro de Prefixo UTM (Aparece apenas se UTM) */}
                                        {dimension === 'utm' && utmPrefixes.length > 0 && (
                                            <FormGroup className="mb-0 mr-2" style={{ minWidth: '120px' }}>
                                                <Input
                                                    type="select"
                                                    value={utmPrefix}
                                                    onChange={(e) => {
                                                        setUtmPrefix(e.target.value);
                                                        // Limpa o valor ao mudar categoria para forçar nova seleção automática ou manual
                                                        setDimensionValue("");
                                                    }}
                                                    className="form-control-alternative"
                                                >
                                                    {utmPrefixes.map((prefix, idx) => (
                                                        <option key={idx} value={prefix}>{prefix}</option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        )}

                                        {/* Filtro de Valor (Site ou UTM) */}
                                        {dimension !== 'general' && (
                                            <FormGroup className="mb-0 mr-2" style={{ minWidth: '200px' }}>
                                                <Input
                                                    type="select"
                                                    value={dimensionValue}
                                                    onChange={(e) => setDimensionValue(e.target.value)}
                                                    className="form-control-alternative"
                                                >
                                                    <option value="" disabled>Selecione...</option>
                                                    {displayOptions.map((opt, idx) => (
                                                        <option key={idx} value={opt}>{opt}</option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        )}
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-4 text-center">
                                    <Col md="6">
                                        <h4 className="text-muted text-uppercase">Receita Total</h4>
                                        <h2 className="text-primary">${totalRevenue}</h2>
                                    </Col>
                                    <Col md="6">
                                        <h4 className="text-muted text-uppercase">Pontos Coletados</h4>
                                        <h2 className="text-info">{pointsCount}</h2>
                                    </Col>
                                </Row>

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" />
                                        <p>Carregando dados...</p>
                                    </div>
                                ) : chartData ? (
                                    <div style={{ width: '100%', height: '350px' }}>
                                        <Line
                                            data={chartData}
                                            options={chartOptions}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-center">Sem dados para exibir.</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default AdManagerHourlyReport;
